import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, Form, Radio, Select } from 'antd';
import { useMusician, useMusicianLinks } from '../api/queries';
import { create_musician_link, update_musician_link, delete_musician_link, update_musician, MusicianLink } from '../api';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useHeader } from '../components/Layout/MainLayout';
import { DefaultAvatar, PlusIcon } from '../components/icons';
import SocialLinksRow, { SocialPlatform } from '../components/SocialLinksRow';
import MusicianLinkCard from '../components/MusicianLinkCard';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import SpecModal from '../components/SpecModal/SpecModal';
import SpecActionSheet from '../components/SpecActionSheet/SpecActionSheet';
import type { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import { useShare } from '../hooks/useShare';
import './MusicianLinks.css';

const MusicianLinks: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { setOnMoreClick } = useHeader();

  const { data: musician, isLoading: musicianLoading } = useMusician(id);
  const { data: links = [], isLoading: linksLoading } = useMusicianLinks(id);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [headerActionSheetOpen, setHeaderActionSheetOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<MusicianLink | null>(null);
  const [saving, setSaving] = useState(false);
  const [linkType, setLinkType] = useState<'custom' | 'social'>('custom');
  const [editingSocialPlatform, setEditingSocialPlatform] = useState<SocialPlatform | null>(null);

  const socialPlatforms = [
    { value: 'instagram', label: 'Instagram', field: 'instagram_url' },
    { value: 'tiktok', label: 'TikTok', field: 'tiktok_url' },
    { value: 'youtube', label: 'YouTube', field: 'youtube_url' },
    { value: 'website', label: 'Website', field: 'website_url' },
  ];

  const toastKeyRef = useRef(0);
  const showToast = useCallback((msg: string) => { toastKeyRef.current += 1; setToastMessage(msg); }, []);
  const share = useShare(showToast);

  const [form] = Form.useForm();

  const fullName = musician ? `${musician.first_name} ${musician.last_name}` : '';

  // Normalize URL by adding https:// if no protocol is present
  const normalizeUrl = (url: string): string => {
    if (!url) return url;
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  // Permissions
  const isAdmin = !!user?.claims?.permissions?.includes('*:*');
  const isOwner = !!musician && !!user && musician.user_id === user.uid;
  const canEdit = isOwner || isAdmin;

  // Connect header "more" button to our action sheet
  useEffect(() => {
    setOnMoreClick(() => setHeaderActionSheetOpen(true));
    return () => setOnMoreClick(undefined);
  }, [setOnMoreClick]);

  // Header action sheet items
  const headerActionItems: ActionSheetItem[] = musician ? [
    { key: 'share', label: 'Share', onClick: () => share({ title: fullName, entityType: 'musician', entityId: id }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); showToast('Link Copied'); } },
    { key: 'view-profile', label: 'View Profile', onClick: () => navigate(`/musicians/${id}`) },
  ] : [];

  const handleAddLink = () => {
    setEditingLink(null);
    setLinkType('custom');
    form.resetFields();
    setLinkModalOpen(true);
  };

  const handleEditLink = (link: MusicianLink) => {
    setEditingLink(link);
    setLinkType('custom');
    form.setFieldsValue({
      title: link.title,
      url: link.url,
    });
    setLinkModalOpen(true);
  };

  const closeModal = () => {
    setLinkModalOpen(false);
    setEditingLink(null);
    setEditingSocialPlatform(null);
    setLinkType('custom');
    form.resetFields();
  };

  const handleDeleteLink = async (link: MusicianLink) => {
    if (!id) return;
    try {
      await delete_musician_link(id, link.id);
      queryClient.invalidateQueries({ queryKey: ['musician', id, 'links'] });
      showToast('Link deleted');
    } catch (err: any) {
      showToast(err.message || 'Failed to delete link');
    }
  };

  const handleSaveLink = async () => {
    if (!id) return;
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (linkType === 'social' || editingSocialPlatform) {
        // Save social link by updating musician profile
        const platformValue = editingSocialPlatform || values.platform;
        const platform = socialPlatforms.find(p => p.value === platformValue);
        if (platform) {
          const normalizedUrl = normalizeUrl(values.socialUrl);
          await update_musician(id, { [platform.field]: normalizedUrl });
          queryClient.invalidateQueries({ queryKey: ['musician', id] });
          showToast(`${platform.label} link saved`);
        }
      } else {
        // Save custom link
        const normalizedUrl = normalizeUrl(values.url);

        if (editingLink) {
          await update_musician_link(id, editingLink.id, {
            title: values.title,
            url: normalizedUrl,
          });
          showToast('Link updated');
        } else {
          await create_musician_link(id, {
            title: values.title,
            url: normalizedUrl,
            order: links.length,
          });
          showToast('Link added');
        }

        queryClient.invalidateQueries({ queryKey: ['musician', id, 'links'] });
      }

      closeModal();
    } catch (err: any) {
      if (err.errorFields) {
        // Form validation error - handled by Ant Form
      } else {
        showToast(err.message || 'Failed to save link');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleShareLink = (link: MusicianLink) => {
    share({ title: link.title, url: link.url });
  };

  const handleCopyLink = (link: MusicianLink) => {
    navigator.clipboard?.writeText(link.url).catch(() => {});
    showToast('Link Copied');
  };

  const handleEditSocialLink = (platform: SocialPlatform, currentUrl: string) => {
    setEditingSocialPlatform(platform);
    setLinkType('social');
    form.setFieldsValue({
      platform,
      socialUrl: currentUrl,
    });
    setLinkModalOpen(true);
  };

  const handleDeleteSocialLink = async (platform: SocialPlatform) => {
    if (!id) return;
    try {
      const platformConfig = socialPlatforms.find(p => p.value === platform);
      if (platformConfig) {
        await update_musician(id, { [platformConfig.field]: null });
        queryClient.invalidateQueries({ queryKey: ['musician', id] });
        showToast(`${platformConfig.label} link removed`);
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to remove link');
    }
  };

  if (musicianLoading) {
    return (
      <div className="musician-links-page">
        <div className="musician-links-page__loading">Loading...</div>
      </div>
    );
  }

  if (!musician) {
    return (
      <div className="musician-links-page">
        <div className="musician-links-page__error">Musician not found</div>
      </div>
    );
  }

  return (
    <div className="musician-links-page">
      <div className="musician-links-page__content">
        {/* Avatar */}
        <div className="musician-links-page__avatar">
          {musician.photo_url ? (
            <img
              className="musician-links-page__avatar-img"
              src={musician.photo_url}
              alt={fullName}
            />
          ) : (
            <div className="musician-links-page__avatar-placeholder">
              <DefaultAvatar size={40} />
            </div>
          )}
        </div>

        {/* Name */}
        <h1 className="musician-links-page__name">{fullName}</h1>

        {/* Social Links Row */}
        <SocialLinksRow
          instagramUrl={musician.instagram_url}
          tiktokUrl={musician.tiktok_url}
          youtubeUrl={musician.youtube_url}
          websiteUrl={musician.website_url}
          isOwner={canEdit}
          onEdit={handleEditSocialLink}
          onDelete={handleDeleteSocialLink}
        />

        {/* Custom Links */}
        <div className="musician-links-page__links">
          {linksLoading ? (
            <div className="musician-links-page__links-loading">Loading links...</div>
          ) : links.length === 0 ? (
            !canEdit && <div className="musician-links-page__links-empty">No links yet</div>
          ) : (
            links.map((link) => (
              <MusicianLinkCard
                key={link.id}
                title={link.title}
                url={link.url}
                isOwner={canEdit}
                onShare={() => handleShareLink(link)}
                onCopyLink={() => handleCopyLink(link)}
                onEdit={() => handleEditLink(link)}
                onDelete={() => handleDeleteLink(link)}
              />
            ))
          )}
        </div>

        {/* Add Link Button (owners only) */}
        {canEdit && (
          <Button
            className="musician-links-page__add-button"
            type="default"
            shape="round"
            size="large"
            icon={<PlusIcon size="s" tone="white" />}
            onClick={handleAddLink}
          >
            Add Link
          </Button>
        )}
      </div>

      {/* Header Action Sheet */}
      <SpecActionSheet
        open={headerActionSheetOpen}
        onClose={() => setHeaderActionSheetOpen(false)}
        items={headerActionItems}
      />

      {/* Add/Edit Link Modal */}
      <SpecModal
        open={linkModalOpen}
        onCancel={closeModal}
        title={
          editingSocialPlatform
            ? `Edit ${socialPlatforms.find(p => p.value === editingSocialPlatform)?.label}`
            : editingLink
              ? 'Edit Link'
              : 'Add Link'
        }
        footer={null}
        centered
        width={340}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveLink}
        >
          {/* Link Type Selection - only show when adding new link (not editing) */}
          {!editingLink && !editingSocialPlatform && (
            <Form.Item label="Link Type">
              <Radio.Group
                value={linkType}
                onChange={(e) => {
                  setLinkType(e.target.value);
                  form.resetFields();
                }}
                optionType="button"
                buttonStyle="solid"
                style={{ width: '100%', display: 'flex' }}
              >
                <Radio.Button value="custom" style={{ flex: 1, textAlign: 'center' }}>Custom Link</Radio.Button>
                <Radio.Button value="social" style={{ flex: 1, textAlign: 'center' }}>Social Link</Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}

          {(linkType === 'social' && !editingLink) || editingSocialPlatform ? (
            <>
              {!editingSocialPlatform && (
                <Form.Item
                  name="platform"
                  label="Platform"
                  rules={[{ required: true, message: 'Please select a platform' }]}
                >
                  <Select placeholder="Select platform">
                    {socialPlatforms.map((platform) => (
                      <Select.Option key={platform.value} value={platform.value}>
                        {platform.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              <Form.Item
                name="socialUrl"
                label="URL"
                rules={[{ required: true, message: 'Please enter a URL' }]}
              >
                <Input placeholder="username or full URL" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="title"
                label="Title"
                rules={[
                  { required: true, message: 'Please enter a title' },
                  { max: 100, message: 'Title must be 100 characters or less' },
                ]}
              >
                <Input placeholder="My Website" />
              </Form.Item>
              <Form.Item
                name="url"
                label="URL"
                rules={[{ required: true, message: 'Please enter a URL' }]}
              >
                <Input placeholder="example.com" />
              </Form.Item>
            </>
          )}

          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            shape="round"
            loading={saving}
          >
            {editingLink ? 'Save Changes' : 'Add Link'}
          </Button>
        </Form>
      </SpecModal>

      <SpecAlert key={toastKeyRef.current} message={toastMessage} onDone={() => setToastMessage(null)} />
    </div>
  );
};

export default MusicianLinks;
